/* ──────────────────────────────────────────────────────────────
 *  Bushing-type master configuration
 *
 *  Single source of truth for per-type behaviour derived from
 *  the Python BushingApp class attributes.
 * ────────────────────────────────────────────────────────────── */

import type { BushingType, BushingTypeConfig, ParamName } from '@/types/bushing';
import type { MaterialType } from '@/types/config';

/* ── Compound families ────────────────────────────────────── */
const COMPOUND_FAMILIES_STANDARD: string[] = ['NR-C023A', 'NR-C120A', 'NR-C020A', 'NR-C010A'];
const COMPOUND_FAMILIES_BYD: string[] = ['NR-C023A'];

/* ── Parameter names per type ─────────────────────────────── */

/** A1, B1, C1 share identical stiffness parameters. */
const PARAM_NAMES_STANDARD: ParamName[] = ['Cz', 'Cx', 'Torsion', 'Conical'];
const PARAM_NAMES_D1: ParamName[] = ['Cy', 'Cz', 'Cx', 'Torsion', 'Conical Void', 'Conical Solid'];
const PARAM_NAMES_BYD_VOIDED: ParamName[] = ['Cy', 'Cz', 'Cx'];
const PARAM_NAMES_BYD_SOLID: ParamName[] = ['Cz', 'Cx'];

/* ── Display labels per type ──────────────────────────────── */

/** A1, B1, C1 share identical display labels. */
const DISPLAY_LABELS_STANDARD = {
  Cx: 'Axial [N/mm]',
  Cz: 'Radial [N/mm]',
  Torsion: 'Torsion [Nm/°]',
  Conical: 'Conical [Nm/°]',
} as const;

const DISPLAY_LABELS_D1 = {
  Cx: 'Axial [N/mm]',
  Cy: 'Radial Void [N/mm]',
  Cz: 'Radial Solid [N/mm]',
  Torsion: 'Torsion [Nm/°]',
  'Conical Void': 'Conical Void [Nm/°]',
  'Conical Solid': 'Conical Solid [Nm/°]',
} as const;

const DISPLAY_LABELS_BYD_VOIDED = {
  Cx: 'Axial [N/mm]',
  Cy: 'Radial Void [N/mm]',
  Cz: 'Radial Solid [N/mm]',
} as const;

const DISPLAY_LABELS_BYD_SOLID = {
  Cx: 'Axial [N/mm]',
  Cz: 'Radial [N/mm]',
} as const;

/* ── Complete per-type config map ─────────────────────────── */

export const BUSHING_TYPE_CONFIGS: Record<BushingType, BushingTypeConfig> = {
  A1: {
    paramNames: PARAM_NAMES_STANDARD,
    displayLabels: DISPLAY_LABELS_STANDARD,
    sleeveThickness: { options: [2.0, 1.5], default: 2.0 },
    hasLinearTravel: false,
    packagingLocked: false,
    targetColumns: 3,
    compoundFamilies: COMPOUND_FAMILIES_STANDARD,
    noAmplitudeParams: ['Conical'],
  },
  B1: {
    paramNames: PARAM_NAMES_STANDARD,
    displayLabels: DISPLAY_LABELS_STANDARD,
    sleeveThickness: { options: [2.0, 1.5], default: 2.0 },
    hasLinearTravel: false,
    packagingLocked: false,
    targetColumns: 3,
    compoundFamilies: COMPOUND_FAMILIES_STANDARD,
    noAmplitudeParams: ['Conical'],
  },
  C1: {
    paramNames: PARAM_NAMES_STANDARD,
    displayLabels: DISPLAY_LABELS_STANDARD,
    sleeveThickness: { options: [2.0, 1.5], default: 2.0 },
    hasLinearTravel: true,
    linearTravelField: { param: 'Cx', boxes: 1 },
    packagingLocked: false,
    targetColumns: 4,
    compoundFamilies: COMPOUND_FAMILIES_STANDARD,
    noAmplitudeParams: ['Conical'],
  },
  D1: {
    paramNames: PARAM_NAMES_D1,
    displayLabels: DISPLAY_LABELS_D1,
    sleeveThickness: { options: [2.5, 2.0], default: 2.5 },
    hasLinearTravel: true,
    linearTravelField: { param: 'Cy', boxes: 2 },
    packagingLocked: false,
    targetColumns: 4,
    compoundFamilies: COMPOUND_FAMILIES_STANDARD,
    noAmplitudeParams: ['Conical Void', 'Conical Solid'],
  },
  'BYD EV Voided': {
    paramNames: PARAM_NAMES_BYD_VOIDED,
    displayLabels: DISPLAY_LABELS_BYD_VOIDED,
    sleeveThickness: { options: [3.5, 2.5, 2.0], default: 2.5 },
    hasLinearTravel: false,
    packagingLocked: true,
    targetColumns: 3,
    compoundFamilies: COMPOUND_FAMILIES_BYD,
    noAmplitudeParams: [],
  },
  'BYD EV Solid': {
    paramNames: PARAM_NAMES_BYD_SOLID,
    displayLabels: DISPLAY_LABELS_BYD_SOLID,
    sleeveThickness: { options: [3.5, 2.5, 2.0], default: 2.5 },
    hasLinearTravel: false,
    packagingLocked: true,
    targetColumns: 3,
    compoundFamilies: COMPOUND_FAMILIES_BYD,
    noAmplitudeParams: [],
  },
};

/** Whether a bushing type is a BYD variant. */
export function isByd(type: BushingType): boolean {
  return type === 'BYD EV Voided' || type === 'BYD EV Solid';
}

/** All supported bushing type keys (useful for dropdowns). */
export const BUSHING_TYPES: BushingType[] = [
  'A1',
  'B1',
  'C1',
  'D1'
];

/** Runtime type guard for values coming from untrusted backend responses. */
export function isValidBushingType(value: unknown): value is BushingType {
  return typeof value === 'string' && (BUSHING_TYPES as string[]).includes(value);
}

const VALID_MATERIALS: readonly MaterialType[] = ['Aluminum', 'Steel'] as const;

/** Runtime type guard for material strings from backend responses. */
export function isValidMaterial(value: unknown): value is MaterialType {
  return typeof value === 'string' && (VALID_MATERIALS as readonly string[]).includes(value);
}

/** Map frontend bushing type labels to backend API format strings. */
export const API_BUSHING_TYPE_MAP: Record<BushingType, string> = {
  'A1': 'A1',
  'B1': 'B1',
  'C1': 'C1',
  'D1': 'D1',
  'BYD EV Voided': 'BYD_EV_VOIDED',
  'BYD EV Solid': 'BYD_EV_SOLID',
};

/** Fixed core ID for all BYD presets [mm]. */
export const BYD_CORE_ID = 12.2;